import {
  DOUYIN_CREATOR_BROWSER_STATE_ORIGIN,
  detectEcommerceAccountSessionStatus,
  fetchEcommerceAccountDetail,
  updateEcommerceAccountSessionStatus,
  type EcommerceAccountDetail,
} from '../api';
import {
  IPC,
  type DouyinImBrowserFlowResult,
  type DouyinImTaskRunRequest,
} from '../../common/ipc/channels';
import { useAppStore } from './index';
import {
  assertEcommerceAccountSessionActive,
  isEcommerceAccountSessionExpired,
} from '../utils/ecommerceAccountSession';

interface EnsureCurrentEcomAccountDetailOptions {
  force?: boolean;
}

interface RunDouyinImBrowserFlowOptions {
  targetUrl: string;
  message: string;
  showWindow?: boolean;
}

interface RunDouyinImTaskOptions extends RunDouyinImBrowserFlowOptions {
  taskKind: DouyinImTaskRunRequest['taskKind'];
  entityId: string;
  ecomAccountId: string;
  replyContentJson?: string;
}

const currentEcomAccountDetailRequests = new Map<string, Promise<EcommerceAccountDetail>>();

function isDemoDataSpace() {
  return useAppStore.getState().dataSpaceState.dataSpace === 'DEMO';
}

function assertNonDemoAuthStateUsage() {
  if (isDemoDataSpace()) {
    throw new Error('演示数据模式下暂不支持该跟进操作');
  }
}

function normalizeCookieString(cookieString: string | null | undefined) {
  return (cookieString || '').replace(/\s+/g, ' ').trim();
}

function parseCreatorLocalStorageItems(rawValue: string | Record<string, unknown> | undefined) {
  if (!rawValue) {
    return undefined;
  }

  try {
    const parsed = typeof rawValue === 'string' ? JSON.parse(rawValue) as unknown : rawValue;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return undefined;
    }

    return Object.entries(parsed).reduce<Record<string, string>>((items, [key, value]) => {
      items[key] = String(value ?? '');
      return items;
    }, {});
  } catch {
    return undefined;
  }
}

async function verifyEcommerceAccountSessionAfterImFailure(ecomAccountId: string, reason: string) {
  const normalizedAccountId = ecomAccountId.trim();
  if (!normalizedAccountId) {
    return;
  }

  try {
    const detectedAccount = await detectEcommerceAccountSessionStatus(normalizedAccountId);
    const store = useAppStore.getState();
    const currentDetail = store.currentEcomAccountDetail;
    if (store.currentEcomAccountId === normalizedAccountId && currentDetail.accountId === normalizedAccountId && currentDetail.data) {
      store.setCurrentEcomAccountDetail({
        ...currentDetail.data,
        ...detectedAccount,
      });
    }

    if (!isEcommerceAccountSessionExpired(detectedAccount)) {
      return;
    }

    await updateEcommerceAccountSessionStatus(normalizedAccountId, {
      sessionStatus: 'EXPIRED',
      message: reason.trim() || '当前抖音账号登录态疑似失效，请重新登录',
    });
  } catch (error) {
    console.warn('Failed to verify ecommerce account session after douyin im failure.', error);
  }
}

export function getDouyinCreatorLocalStorageItemsFromAccountDetail(
  accountDetail: EcommerceAccountDetail,
  cookieString: string
) {
  if (isDemoDataSpace()) {
    return undefined;
  }
  const currentCookie = normalizeCookieString(cookieString);
  if (!currentCookie || normalizeCookieString(accountDetail.cookie) !== currentCookie) {
    return undefined;
  }

  const browserState = accountDetail.browserState ?? accountDetail.browser_state;
  const localStorageByOrigin = browserState?.localStorageByOrigin ?? browserState?.local_storage_by_origin ?? {};
  return parseCreatorLocalStorageItems(localStorageByOrigin[DOUYIN_CREATOR_BROWSER_STATE_ORIGIN]);
}

export async function ensureCurrentEcomAccountDetail(
  options: EnsureCurrentEcomAccountDetailOptions = {}
): Promise<EcommerceAccountDetail> {
  const { force = false } = options;
  const store = useAppStore.getState();
  const currentAccountId = store.currentEcomAccountId.trim();

  if (!currentAccountId) {
    throw new Error('当前未选择已绑定的跟进账号');
  }

  const cachedDetail = store.currentEcomAccountDetail;
  if (
    !force &&
    cachedDetail.accountId === currentAccountId &&
    cachedDetail.data
  ) {
    assertEcommerceAccountSessionActive(cachedDetail.data);
    return cachedDetail.data;
  }

  const existingRequest = currentEcomAccountDetailRequests.get(currentAccountId);
  if (existingRequest) {
    return existingRequest;
  }

  store.setCurrentEcomAccountDetailLoading(currentAccountId, true);

  const nextRequest = fetchEcommerceAccountDetail(currentAccountId)
    .then((detail) => {
      assertEcommerceAccountSessionActive(detail);
      // 账号切换后，旧请求返回的数据不应该覆盖当前激活账号详情。
      if (useAppStore.getState().currentEcomAccountId === currentAccountId) {
        useAppStore.getState().setCurrentEcomAccountDetail(detail);
      }
      return detail;
    })
    .catch((error: unknown) => {
      const nextMessage = error instanceof Error ? error.message : '加载电商账号详情失败';
      if (useAppStore.getState().currentEcomAccountId === currentAccountId) {
        useAppStore.getState().setCurrentEcomAccountDetailError(currentAccountId, nextMessage);
      }
      throw error;
    })
    .finally(() => {
      currentEcomAccountDetailRequests.delete(currentAccountId);
    });

  currentEcomAccountDetailRequests.set(currentAccountId, nextRequest);
  return nextRequest;
}

async function ensureEcomAccountDetailById(
  ecomAccountId: string,
  options: EnsureCurrentEcomAccountDetailOptions = {}
): Promise<EcommerceAccountDetail> {
  const { force = false } = options;
  const store = useAppStore.getState();
  const accountId = ecomAccountId.trim();
  if (!accountId) {
    throw new Error('当前未选择已绑定的跟进账号');
  }

  const cachedDetail = store.currentEcomAccountDetail;
  if (!force && cachedDetail.accountId === accountId && cachedDetail.data) {
    assertEcommerceAccountSessionActive(cachedDetail.data);
    return cachedDetail.data;
  }

  const existingRequest = currentEcomAccountDetailRequests.get(accountId);
  if (existingRequest) {
    return existingRequest;
  }

  store.setCurrentEcomAccountDetailLoading(accountId, true);

  const nextRequest = fetchEcommerceAccountDetail(accountId)
    .then((detail) => {
      assertEcommerceAccountSessionActive(detail);
      if (useAppStore.getState().currentEcomAccountId === accountId) {
        useAppStore.getState().setCurrentEcomAccountDetail(detail);
      }
      return detail;
    })
    .catch((error: unknown) => {
      const nextMessage = error instanceof Error ? error.message : '加载电商账号详情失败';
      if (useAppStore.getState().currentEcomAccountId === accountId) {
        useAppStore.getState().setCurrentEcomAccountDetailError(accountId, nextMessage);
      }
      throw error;
    })
    .finally(() => {
      currentEcomAccountDetailRequests.delete(accountId);
    });

  currentEcomAccountDetailRequests.set(accountId, nextRequest);
  return nextRequest;
}

export async function runDouyinImBrowserFlowWithCurrentAccount(
  options: RunDouyinImBrowserFlowOptions
): Promise<DouyinImBrowserFlowResult> {
  if (!window.electronAPI) {
    throw new Error('浏览器私信仅在桌面版中可用');
  }
  assertNonDemoAuthStateUsage();

  const store = useAppStore.getState();
  const currentAccountDetail = await ensureCurrentEcomAccountDetail({ force: true });
  const cookieString = currentAccountDetail.cookie?.trim();
  if (!cookieString) {
    throw new Error('当前账号授权状态不可用，请重新授权后重试');
  }

  // 业务页默认跟随全局调试开关；设置页等手动调试入口可显式覆盖。
  const showWindow = options.showWindow ?? store.douyinImDebug.enabled;

  try {
    const result = await window.electronAPI.invoke(IPC.RUN_DOUYIN_IM_BROWSER_FLOW, {
      targetUrl: options.targetUrl,
      message: options.message,
      showWindow,
      cookieString,
      cookieSource: `api-ecommerce-account-detail:${currentAccountDetail.ecom_account_id}`,
    }) as DouyinImBrowserFlowResult;
    if (!result.success) {
      await verifyEcommerceAccountSessionAfterImFailure(
        currentAccountDetail.ecom_account_id,
        result.error || '当前抖音账号登录态疑似失效，请重新登录'
      );
    }
    return result;
  } catch (error) {
    await verifyEcommerceAccountSessionAfterImFailure(
      currentAccountDetail.ecom_account_id,
      error instanceof Error ? error.message : '当前抖音账号登录态疑似失效，请重新登录'
    );
    throw error;
  }
}

export async function runDouyinImTaskWithAccount(
  options: RunDouyinImTaskOptions
): Promise<DouyinImBrowserFlowResult> {
  if (!window.electronAPI) {
    throw new Error('浏览器私信仅在桌面版中可用');
  }
  assertNonDemoAuthStateUsage();

  const store = useAppStore.getState();
  const appUserId = store.auth.userId.trim();
  if (!appUserId) {
    throw new Error('当前登录态无效，请重新登录后重试');
  }

  const currentAccountDetail = await ensureEcomAccountDetailById(options.ecomAccountId, { force: true });
  const cookieString = currentAccountDetail.cookie?.trim();
  if (!cookieString) {
    throw new Error('当前账号授权状态不可用，请重新授权后重试');
  }

  const showWindow = options.showWindow ?? store.douyinImDebug.enabled;

  try {
    const result = await window.electronAPI.invoke(IPC.RUN_DOUYIN_IM_TASK, {
      taskKind: options.taskKind,
      appUserId,
      entityId: options.entityId,
      ecomAccountId: options.ecomAccountId,
      targetUrl: options.targetUrl,
      message: options.message,
      replyContentJson: options.replyContentJson,
      showWindow,
      cookieString,
      cookieSource: `api-ecommerce-account-detail:${currentAccountDetail.ecom_account_id}`,
      sessionKey: `${appUserId}:${options.ecomAccountId}`,
    }) as DouyinImBrowserFlowResult;
    if (!result.success) {
      await verifyEcommerceAccountSessionAfterImFailure(
        currentAccountDetail.ecom_account_id,
        result.error || '当前抖音账号登录态疑似失效，请重新登录'
      );
    }
    return result;
  } catch (error) {
    await verifyEcommerceAccountSessionAfterImFailure(
      currentAccountDetail.ecom_account_id,
      error instanceof Error ? error.message : '当前抖音账号登录态疑似失效，请重新登录'
    );
    throw error;
  }
}
